import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Search } from 'lucide-react';
import { useSearch } from '@/contexts/SearchContext';
import { Sidebar } from '@/components/layout/Sidebar';
import LeadNotificationsBell from '@/components/crm/LeadNotificationsBell';
import IPDBillEditRequestsBell from '@/components/ipd/IPDBillEditRequestsBell';
import LeadFollowUpReminderDialog from '@/components/crm/LeadFollowUpReminderDialog';
import LeadCallNowAlertDialog from '@/components/crm/LeadCallNowAlertDialog';
import NewLeadAlertPopup from '@/components/crm/NewLeadAlertPopup';
import CrmLeadActivitySync from '@/components/crm/CrmLeadActivitySync';
import SarvamReminderConfirmedAlertDialog from '@/components/crm/SarvamReminderConfirmedAlertDialog';
import { ChainHospitalPicker } from '@/components/chain/ChainHospitalPicker';
import { isCrmViewOnlyUser, isExternalPartner } from '@/utils/crmAccess';
import {
  businessAnalystSidebarGroups,
  chainManagementSidebarGroup,
  hospitalAdminSidebarGroups,
} from '@/config/hospitalAdminSidebar';

// Define the role-specific configurations
const doctorPortalSections = [
  { name: 'Dashboard', path: '/doctor/dashboard' },
  { name: 'Appointments', path: '/doctor/appointments' },
  { name: 'Day Care', path: '/doctor/daycare' },
  { name: 'IPD Management', path: '/doctor/ipd-management' },
  { name: 'Disease Templates', path: '/doctor/template' },
  { name: 'Create Test', path: '/doctor/create' },
];

const rmoPortalSections = [
  { name: 'Dashboard', path: '/doctor/dashboard' },
  { name: 'Appointments', path: '/doctor/appointments' },
  { name: 'Day Care', path: '/doctor/daycare' },
  { name: 'All IPD Patients', path: '/doctor/ipd-management' },
  { name: 'Disease Templates', path: '/doctor/template' },
  { name: 'Create Test', path: '/doctor/create' },
];

const doctorTeamLeadPortalSections = [
  { name: 'Team & performance', kind: 'header' as const },
  { name: 'Team Dashboard', path: '/doctor-team/dashboard' },
  { name: 'Clinical OPD', kind: 'header' as const },
  ...doctorPortalSections,
];

const roleConfigs = {
  DOCTOR_TEAM_LEAD: {
    basePath: '/doctor-team',
    sections: doctorTeamLeadPortalSections,
    searchPlaceholders: {
      appointments: 'Search Patients',
      default: 'Search…',
    },
  },
  DOCTOR: {
    basePath: '/doctor',
    sections: doctorPortalSections,
    searchPlaceholders: {
      appointments: 'Search Patients',
      default: 'Search...',
    },
  },
  RMO: {
    basePath: '/doctor',
    sections: rmoPortalSections,
    searchPlaceholders: {
      appointments: 'Search Patients',
      default: 'Search...',
    },
  },
  HOSPITAL_ADMIN: {
    basePath: '/hospital-admin',
    sections: hospitalAdminSidebarGroups,
    searchPlaceholders: {
      'opd-fees': 'Search doctors by name or specialisation',
      users: 'Search users by name',
      patients: 'Search patients by name',
      shifts: 'Search shifts',
      'business-reports': 'Filter reports',
      intelligence: 'Command Centre',
      default: 'Search...',
    },
  },
  RECEPTIONIST: {
    basePath: '/receptionist',
    sections: [
      { name: 'Overview', kind: 'header' as const },
      { name: 'Dashboard', path: '/receptionist/dashboard' },

      { name: 'Front desk', kind: 'header' as const },
      { name: 'Patients', path: '/receptionist/patients' },
      { name: 'Patient Bills', path: '/receptionist/patient-bills' },
      { name: 'Appointments', path: '/receptionist/appointments' },
      { name: 'Self Registration QR', path: '/receptionist/self-register-qr' },

      { name: 'Inpatient & day care', kind: 'header' as const },
      { name: 'IPD Queue', path: '/receptionist/ipd-queue' },
      { name: 'IPD Offline Billing', path: '/receptionist/ipd-offline-queue' },
      { name: 'IPD Pending Collection', path: '/receptionist/ipd-pending-collection' },
      { name: 'Day Care', path: '/receptionist/daycare' },

      { name: 'Clinical support', kind: 'header' as const },
      { name: 'Pending Lab Test Bills', path: '/receptionist/pending-lab-bills' },
      { name: 'Create Test', path: '/receptionist/create-test' },
      { name: 'Surgical Appointments', path: '/receptionist/surgical-appointments' },

      { name: 'Follow-ups', kind: 'header' as const },
      { name: 'OPD Follow Ups', path: '/receptionist/follow-ups' },
      { name: 'IPD Discharge Follow-ups', path: '/receptionist/ipd-discharge-follow-ups' },

      { name: 'Finance', kind: 'header' as const },
      { name: 'Expenses', path: '/receptionist/expenses' },
      { name: 'Reconciliation', path: '/receptionist/reconciliation' },
    ],
    searchPlaceholders: {
      appointments: 'Search appointments',
      patients: 'Search patients',
      'follow-ups': 'Search OPD follow-ups',
      'ipd-discharge-follow-ups': 'Search IPD discharge follow-ups',
      default: 'Search...',
    },
  },
  OPERATIONS_MANAGER: {
    basePath: '/receptionist',
    sections: [
      { name: 'Overview', kind: 'header' as const },
      { name: 'Dashboard', path: '/receptionist/dashboard' },
      { name: 'Queue Management', path: '/receptionist/queue-management' },

      { name: 'Front desk', kind: 'header' as const },
      { name: 'Patients', path: '/receptionist/patients' },
      { name: 'Appointments', path: '/receptionist/appointments' },

      { name: 'Inpatient & day care', kind: 'header' as const },
      { name: 'IPD Queue', path: '/receptionist/ipd-queue' },
      { name: 'IPD Offline Billing', path: '/receptionist/ipd-offline-queue' },
      { name: 'IPD Pending Collection', path: '/receptionist/ipd-pending-collection' },
      { name: 'Day Care', path: '/receptionist/daycare' },

      { name: 'Follow-ups', kind: 'header' as const },
      { name: 'IPD Discharge Follow-ups', path: '/receptionist/ipd-discharge-follow-ups' },

      { name: 'Reports', kind: 'header' as const },
      { name: 'Customer Voice', path: '/receptionist/customer-voice' },
      { name: 'Summary Report', path: '/receptionist/business-reports' },

      { name: 'Finance', kind: 'header' as const },
      { name: 'Expenses', path: '/receptionist/expenses' },
      { name: 'Vendor payments', path: '/receptionist/vendor-payments' },
    ],
    searchPlaceholders: {
      appointments: 'Search appointments',
      patients: 'Search patients',
      expenses: 'Search expenses',
      'customer-voice': 'Customer Voice',
      'business-reports': 'Summary report',
      'queue-management': 'Search queue',
      'ipd-queue': 'Search IPD patients',
      'ipd-offline-queue': 'Search offline IPD patients',
      'ipd-pending-collection': 'Search pending collections',
      daycare: 'Search day care cases',
      'vendor-payments': 'Search vendor invoices',
      'ipd-discharge-follow-ups': 'Search IPD discharge follow-ups',
      default: 'Search...',
    },
  },
  VENDOR: {
    basePath: '/vendor',
    sections: [
      { name: 'Dashboard', path: '/vendor/dashboard' },
      { name: 'My invoices', path: '/vendor/dashboard' },
    ],
    searchPlaceholders: {
      default: 'Search…',
    },
  },
  PHARMA_VENDOR: {
    basePath: '/pharma-vendor',
    sections: [{ name: 'Purchase orders', path: '/pharma-vendor/orders' }],
    searchPlaceholders: {
      default: 'Search…',
    },
  },
  BUSINESS_ANALYST: {
    basePath: '/hospital-admin',
    sections: businessAnalystSidebarGroups,
    searchPlaceholders: {
      'ipd-queue': 'Search IPD patients',
      'ipd-pending-collection': 'Search pending collections',
      leads: 'Search leads',
      expenses: 'Search expenses',
      'business-reports': 'Filter reports',
      intelligence: 'Command Centre',
      'vendor-payments': 'Search vendor invoices',
      default: 'Search...',
    },
  },
  SALES_PERSON: {
    basePath: '/sales-person',
    sections: [
      { name: 'Dashboard', path: '/sales-person/dashboard' },
      { name: 'Leads', path: '/sales-person/leads' },
      { name: 'IPD Cases', path: '/sales-person/crm-ipd-cases' },
      { name: 'IPD Admitted', path: '/sales-person/crm-ipd-admitted' },
      { name: 'Patients', path: '/sales-person/patients' },
      { name: 'Appointments', path: '/sales-person/appointments' },
      { name: 'Follow Ups', path: '/sales-person/follow-ups' },
      { name: 'Surgical Appointments', path: '/sales-person/surgical-appointments' },
      { name: 'Lead Insights', path: '/sales-person/lead-insights' },
      { name: 'AI Outcomes', path: '/sales-person/ai-outcomes' },
      ],
    searchPlaceholders: {
      appointments: 'Search appointments',
      patients: 'Search patients',
      default: 'Search...',
    },
  },
  SALES_MANAGER: {
    basePath: '/sales-person',
    sections: [
      { name: 'Dashboard', path: '/sales-person/dashboard' },
      { name: 'Queue Management', path: '/sales-person/queue-management' },
      { name: 'Leads', path: '/sales-person/leads' },
      { name: 'Sales Report', path: '/sales-person/sales-report' },
      { name: 'IPD Cases', path: '/sales-person/crm-ipd-cases' },
      { name: 'IPD Admitted', path: '/sales-person/crm-ipd-admitted' },
      { name: 'Patients', path: '/sales-person/patients' },
      { name: 'Appointments', path: '/sales-person/appointments' },
      { name: 'Follow Ups', path: '/sales-person/follow-ups' },
      { name: 'Surgical Appointments', path: '/sales-person/surgical-appointments' },
      { name: 'Lead Insights', path: '/sales-person/lead-insights' },
      { name: 'AI Outcomes', path: '/sales-person/ai-outcomes' },
      { name: 'Mini CRM', path: '/hospital-admin/mini-crm-settings' },
      { name: 'Sarvam integrations', path: '/hospital-admin/integrations/sarvam' },
      { name: 'WhatsApp Campaigns', path: '/hospital-admin/whatsapp-campaigns' },
      { name: 'Media Library', path: '/hospital-admin/whatsapp-media-library' },
      { name: 'Meta Dashboard', path: '/hospital-admin/marketing/meta-dashboard' },
      { name: 'Meta Event Logs', path: '/hospital-admin/marketing/meta-event-logs' },
      { name: 'Google Event Logs', path: '/hospital-admin/marketing/google-event-logs' },
      { name: 'Meta Attribution', path: '/hospital-admin/marketing/meta-attribution' },
    ],
    searchPlaceholders: {
      appointments: 'Search appointments',
      patients: 'Search patients',
      default: 'Search...',
    },
  },
  EXTERNAL_PARTNER: {
    basePath: '/sales-person',
    sections: [
      { name: 'Leads', path: '/sales-person/leads' },
      { name: 'IPD Cases', path: '/sales-person/crm-ipd-cases' },
      { name: 'Lead Insights', path: '/sales-person/lead-insights' },
    ],
    searchPlaceholders: {
      default: 'Search leads...',
    },
  },
  LAB_TECHNICIAN: {
    basePath: '/lab',
    sections: [
      { name: 'Dashboard', path: '/lab/dashboard' },
      { name: 'From Doctors', path: '/lab/from-doctors' },
      { name: 'From Receptionist', path: '/lab/from-receptionist' },
      { name: 'IPD Tests', path: '/lab/ipd-tests' },
      { name: 'Completed Tests', path: '/lab/completed-tests' },
      { name: 'Reports', path: '/lab/reports' },
      { name: 'Create Test', path: '/lab/create' },
    ],
    searchPlaceholders: {
      default: 'Search...',
    },
  },
  NURSE: {
    basePath: '/nurse',
    sections: [
      { name: 'Dashboard', path: '/nurse/dashboard' },
      { name: 'IPD Management', path: '/nurse/ipd-management' },
    ],
    searchPlaceholders: {
      'ipd-management': 'Search patients by name or UHID',
      default: 'Search...',
    },
  },
  PHARMACIST: {
    basePath: '/pharmacist',
    sections: [
      { name: 'Dashboard', path: '/pharmacist/dashboard' },
      { name: 'Prescription Queue', path: '/pharmacist/queue' },
      { name: 'IPD Pharma', path: '/pharmacist/ipd-pharma' },
      { name: 'Stock & Batches', path: '/pharmacist/stock' },
      { name: 'Stock In / Purchase', path: '/pharmacist/stock-in' },
      { name: 'Purchase orders', path: '/pharmacist/purchase-orders' },
      { name: 'Vendors', path: '/pharmacist/vendors' },
      {
        name: 'OT Management',
        kind: 'group' as const,
        children: [
          { name: 'OT Dashboard', path: '/pharmacist/ot' },
          { name: 'Transfer to OT', path: '/pharmacist/ot/transfer' },
          { name: 'OT Consumption', path: '/pharmacist/ot/consumption' },
          { name: 'OT General Use', path: '/pharmacist/ot/general' },
          { name: 'OT Reconciliation', path: '/pharmacist/ot/reconcile' },
          { name: 'OT Reports', path: '/pharmacist/ot/reports' },
        ],
      },
      { name: 'Walk-in Sale', path: '/pharmacist/walk-in-sale' },
      { name: 'Sales Report', path: '/pharmacist/sales-report' },
      { name: 'Returns', path: '/pharmacist/returns' },
      { name: 'Return History', path: '/pharmacist/return-history' },
    ],
    searchPlaceholders: {
      queue: 'Search by patient or prescription',
      stock: 'Search drug by name',
      default: 'Search...',
    },
  },
  SUPER_ADMIN: {
    basePath: '/super-admin',
    sections: [
      { name: 'Dashboard', path: '/super-admin/dashboard' },
      { name: 'Tenants', path: '/super-admin/tenants' },
      { name: 'Hospitals', path: '/super-admin/hospitals' },
      { name: 'Admins', path: '/super-admin/admins' },
      { name: 'Command Centre', path: '/super-admin/intelligence' },
      { name: 'Business Reports', path: '/super-admin/business-reports' },
      { name: 'Pending Payments', path: '/super-admin/pending-payments' },
      { name: 'Operational Reports', path: '/super-admin/operational-reports' },
      { name: 'Customer Voice', path: '/super-admin/customer-voice' },
      { name: 'Lab Reports', path: '/super-admin/lab-reports' },
      { name: 'Partner approvals', path: '/super-admin/referral-partners' },
      { name: 'Lead Insights', path: '/super-admin/lead-insights' },
      { name: 'Leads', path: '/super-admin/leads' },
      { name: 'Marketing', kind: 'header' as const },
      { name: 'Meta Integration', path: '/super-admin/marketing/meta-integration' },
      { name: 'Google Integration', path: '/super-admin/marketing/google-integration' },
      { name: 'Expenses (view)', path: '/super-admin/expenses' },
      { name: 'Additional Revenue', path: '/super-admin/additional-revenue' },
      { name: 'Receivables', path: '/super-admin/receivables' },
      { name: 'Accounting', path: '/super-admin/accounting' },
      { name: 'GST Management', path: '/super-admin/gst-management' },
    ],
    searchPlaceholders: {
      hospitals: 'Search hospitals',
      users: 'Search users',
      default: 'Search...',
    },
  },
};

export default function UnifiedLayout() {
  const { user, isChainAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { searchQuery, setSearchQuery } = useSearch();

  const isAgencySales =
    user?.role === 'SALES_PERSON' && !!(user as { isLeadAgency?: boolean }).isLeadAgency;
  const crmViewOnly = isCrmViewOnlyUser(user);

  // Get the role-specific configuration; agency sales logins only see Leads + Lead Insights
  const baseRoleConfig = roleConfigs[user?.role as keyof typeof roleConfigs] || roleConfigs.DOCTOR;

  const roleConfig = useMemo(() => {
    if (isAgencySales && user?.role === 'SALES_PERSON') {
      return {
        ...baseRoleConfig,
        sections: [
          { name: 'Leads', path: '/sales-person/leads' },
          { name: 'Lead Insights', path: '/sales-person/lead-insights' },
        ],
      };
    }
    if (isChainAdmin && user?.role === 'HOSPITAL_ADMIN') {
      return {
        ...roleConfigs.HOSPITAL_ADMIN,
        sections: [chainManagementSidebarGroup, ...hospitalAdminSidebarGroups],
      };
    }
    return baseRoleConfig;
  }, [baseRoleConfig, isAgencySales, isChainAdmin, user?.role]);

  useEffect(() => {
    if (!isExternalPartner(user)) return;
    const allowed = [
      '/sales-person/leads',
      '/sales-person/crm-ipd-cases',
      '/sales-person/lead-insights',
    ];
    if (!allowed.some((p) => location.pathname.startsWith(p))) {
      navigate('/sales-person/leads', { replace: true });
    }
  }, [location.pathname, navigate, user]);

  // Reset search query when route changes
  useEffect(() => {
    setSearchQuery('');
  }, [location.pathname, setSearchQuery]);

  // Get the placeholder text based on current route
  const getSearchPlaceholder = () => {
    const path = location.pathname;
    const currentSection = Object.entries(roleConfig.searchPlaceholders).find(([key]) =>
      path.includes(`/${key}`)
    );
    return currentSection ? currentSection[1] : roleConfig.searchPlaceholders.default;
  };

  return (
    <div className="artis-theme min-h-screen flex bg-artis-cream font-artis-sans text-artis-brown-dark">
      {/* Sidebar */}
      <aside className="fixed top-0 left-0 z-50 flex h-screen w-48 flex-col border-r border-artis-brown/10 bg-artis-cream shadow-lg pointer-events-auto">
        <Sidebar sections={roleConfig.sections} />
      </aside>

      {/* Main layout */}
      <div className="relative z-0 ml-48 flex flex-1 flex-col">
        <header className="fixed left-48 right-0 top-0 z-10 flex h-16 items-center justify-between gap-4 border-b border-artis-brown/10 bg-artis-brown-dark px-4 shadow-md sm:px-6">
          <div className="flex min-w-0 flex-1 items-center">
            <div className="relative min-w-0 flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-artis-slate" />
              <input
                type="text"
                placeholder={getSearchPlaceholder()}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-full border border-artis-brown/15 bg-artis-cream/95 py-2 pl-10 pr-4 text-sm text-artis-brown-dark placeholder:text-artis-slate focus:outline-none focus:ring-2 focus:ring-artis-gold/40"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <ChainHospitalPicker />
            {user?.role === 'SALES_PERSON' && !crmViewOnly && (
              <>
                <LeadNotificationsBell />
                <LeadFollowUpReminderDialog />
              </>
            )}
            {user?.role === 'HOSPITAL_ADMIN' && <IPDBillEditRequestsBell />}
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                user?.name || ''
              )}&background=B07A3E&color=fff`}
              alt={user?.name}
              className="h-9 w-9 rounded-full ring-2 ring-artis-gold/30"
            />
            <div className="text-right">
              <p className="font-medium text-artis-cream">{user?.name}</p>
              <p className="text-xs uppercase tracking-wide text-artis-sand/80">
                {user?.role?.replace(/_/g, ' ')}
              </p>
            </div>
          </div>
        </header>

        <main className="thms-internal mt-16 overflow-auto bg-artis-cream p-4 sm:p-6 artis-theme">
          <Outlet />
        </main>

        <CrmLeadActivitySync />
        {(user?.role === 'SALES_PERSON' || user?.role === 'SALES_MANAGER') && !crmViewOnly && (
          <>
            <LeadCallNowAlertDialog />
            <NewLeadAlertPopup />
            <SarvamReminderConfirmedAlertDialog />
          </>
        )}
      </div>
    </div>

  );
} 